<template>
  <i
    v-if="isRelevant"
    class="tm-audio-text v-tm-move-disable"
    :class="classObj"
  >
    <template v-if="hasTranscript">
      <canvas
        ref="display"
        class="-display"
        :style="{ width: `${$data.canvasWidth}px` }"
      />
    </template>
  </i>
</template>

<script>
import {
  getTrackSpanTranscriptionsTokens,
  drawSentencesInMainThread,
} from 'v/Story/Timeline/api/transcription';
import timemachineTaskManager from 'v/Story/Timeline/api/timemachineTaskManager';
import gridMixin from 'v/Story/Timeline/view/grid.mixin';
import fg from '#/libs/FrameGrid';
import { serialInvocation } from '#/worker/toolkit';
import { mapState } from 'vuex';

export default {
  name: 'tm-audio-text',
  mixins: [gridMixin],

  data() {
    return {
      canvasWidth: 0,
    };
  },

  props: {
    track: { type: Object, required: true },
    rectangle: { type: Object, required: true },
    isFirst: { type: Boolean, required: true },
    isLast: { type: Boolean, required: true },
    audioTranscript: { type: Object, required: false, default: null },
    isSelected: { type: Boolean, required: true },
    isHovered: { type: Boolean, required: true },
    isMoving: { type: Boolean, required: true },
  },

  computed: {
    ...mapState('timemachine', {
      pxTranscriptTrackH: (state) => state.cssVariables.pxTranscriptTrackH,
      MODE_AUDIO_DND: (state) => state.MODE.AUDIO_DND,
      MODE_AUDIO_RESIZE: (state) => state.MODE.AUDIO_RESIZE,
    }),

    isRelevant() {
      return this.hasTranscript;
    },

    classObj() {
      const alignRight =
        (this.MODE_AUDIO_DND && !this.$props.isFirst && this.$props.isLast) ||
        (this.MODE_AUDIO_RESIZE && this.$props.track.resizeEscort.fromLeft);

      return {
        '-align-right': alignRight,
      };
    },

    hasTranscript() {
      return !!this.$props.audioTranscript?.tokens?.length;
    },

    relevantTokens() {
      const asset = this.$props.track.data;
      const tokens = getTrackSpanTranscriptionsTokens(
        this.$props.audioTranscript?.tokens,
        {
          displayStartTime: asset.displayStartTime,
          displayEndTime: asset.displayEndTime,
          startTime: asset.relativeStartTime,
        },
        this.$props.rectangle
      );

      return tokens;
    },

    sentenceMaxDuration() {
      return this.rowDuration <= 10 ? 0 : this.rowDuration / 8;
    },

    isSentenceResolution() {
      // as opposed to token resolution
      return this.sentenceMaxDuration > 0;
    },

    continuum() {
      return this.grid[0].continuum;
    },

    sentences() {
      const { data } = this.$props.track;
      let timeAccumulated = 0;
      const sentences = [];
      let sentenceText = [];
      let startTime = null;
      let endTime = null;
      const lastIndex = this.relevantTokens.length - 1;

      for (let n = 0, N = this.relevantTokens.length; n < N; n++) {
        const token = this.relevantTokens[n];

        if (0 === timeAccumulated) {
          sentenceText = [];
          startTime = token.startTime;
        }
        endTime = token.endTime;
        sentenceText.push(token.content);
        timeAccumulated += token.endTime - token.startTime;

        if (timeAccumulated > this.sentenceMaxDuration || n === lastIndex) {
          const shift = ~~fg.trunc(
            (data.displayStartTime +
              startTime -
              this.$props.rectangle.startTime -
              data.relativeStartTime) /
              this.continuum
          );
          const width = ~~fg.trunc(
            Math.min(
              (endTime - startTime) / this.continuum,
              this.$props.rectangle.width - shift
            )
          );

          sentences.push({
            shift,
            width,
            text: sentenceText.join(' '),
          });

          timeAccumulated = 0;
        }
      }

      return sentences;
    },

    renderContextState() {
      if (this.$props.isMoving) {
        return 'MOVING';
      } else if (this.$props.isSelected) {
        return 'SELECTED';
      } else if (this.$props.isHovered) {
        return 'HOVERED';
      } else {
        return 'IDLE';
      }
    },

    canvasColours() {
      if (/HOVERED|SELECTED/.test(this.renderContextState)) {
        return {
          textColour: 'rgb(6,169,244)',
          bgColour: '#f2f2f2',
        };
      } else if ('MOVING' === this.renderContextState) {
        return {
          textColour: 'white',
          bgColour: 'rgb(6,169,244)',
        };
      } else {
        return {
          textColour: 'rgba(0,0,0, 0.7)',
          bgColour: '#f2f2f2',
        };
      }
    },

    scale() {
      const factor = window.devicePixelRatio ?? 1;
      // is 2 on retina display
      return {
        factor,
        spanWidth: this.$props.rectangle.width,
        width: factor * this.$props.rectangle.width,
        height: factor * this.pxTranscriptTrackH,
        fontSize: factor * 12,
        isSentenceResolution: this.isSentenceResolution,
        paddingRight: 50, // px between sentences
        state: this.renderContextState,
        ...this.canvasColours,
      };
    },

    drawDependency() {
      return `${this.isRelevant ? 1 : 0}${this.scale.state}${
        this.scale.width
      }${_.reduce(
        this.sentences,
        (rv, s) => {
          return `${rv}${s.shift}${s.width}${s.text}`;
        },
        ''
      )}${this.scale.factor}${this.scale.isSentenceResolution}`;
    },
  },

  created() {
    this.$_drawSentencesInWorker = serialInvocation(
      this.$_drawSentencesInWorkerTarget,
      this.$_drawBitmap
    );
  },

  watch: {
    drawDependency: {
      deep: false,
      immediate: true,
      handler: '$_drawIfNeeded',
    },
  },

  methods: {
    $_drawIfNeeded() {
      if (!this._isDestroyed && this.hasTranscript && this.isRelevant) {
        if (this.$refs.display && this.$_drawSentencesInWorker) {
          this.$_displaySentences();
        } else {
          this.$nextTick(this.$_drawIfNeeded); // async recursion
        }
      }
    },

    $_displaySentences() {
      if (window.OffscreenCanvas) {
        this.$_drawSentencesInWorker();
      } else {
        // for example: FF, Safari
        drawSentencesInMainThread(
          this.$refs.display,
          this.scale,
          this.sentences
        );
        this.$data.canvasWidth = this.$props.rectangle.width;
      }
    },

    $_drawSentencesInWorkerTarget() {
      return timemachineTaskManager.renderTranscript({
        scale: this.scale,
        sentences: this.sentences,
      });
    },

    $_drawBitmap({ bitmap, width }) {
      if (!this._isDestroyed && this.$refs.display) {
        const canvas = this.$refs.display;
        const bitmapContext = canvas.getContext('bitmaprenderer');
        bitmapContext.transferFromImageBitmap(bitmap);
        this.$data.canvasWidth = width;
      }
    },
  },
};
</script>

<style lang="scss">
.tm-audio-span > .tm-audio-text {
  position: absolute;
  pointer-events: none;

  &.-align-right > .-display {
    right: 0;
  }

  > .-display {
    position: absolute;
    top: calc(-1 * var(--tm-pxTranscriptTrackH));
    height: var(--tm-pxTranscriptTrackH);
    pointer-events: all;
    cursor: default;
  }
}
</style>
